<template>
    <div>
        <slot></slot>
    </div>
</template>
<script>
import mBase from './m-base'
import initAMap from './init-amap'

// 地图
export default {
    name: 'AMap',
    mixins: [mBase],
    provide() {
        return {
            amap: this,
        }
    },
    props: {
        // 设置地图显示的缩放级别
        zoom: {
            type: Number,
        },
        // 地图显示的缩放级别范围，PC默认为[3-18]、手机平板[3-17]
        /* 基础图层level说明
			[
				[3, "国", "1000km", "国级"]
				[4, "首都", "500km"]
				[5, "省会", "200km"]
				[6, "市", "100km", "省级"]
				[7, "市", "100km"]
				[8, "县", "50km"]
				[9, "国道G", "20km"]
				[10, "乡镇", "10km"]
				[11, "省道S", "5km", "市级"]
				[12, "村", "2km"]
				[13, "村", "1km"]
				[14, "行政区", "500m"]
				[15, "县道X", "200m"]
				[16, "乡道Y", "100m", "街级"]
				[17, "街道", "100m"]
				[18, "门牌号", "50m"]
			] */
        zooms: {
            type: Array,
        },
        // 设置地图中心点坐标，默认为当前用户所在市
        center: {
            type: [Array, Object],
        },
        // 地图标注显示顺序，大于110即可将底图上的默认标注显示在覆盖物（圆、折线、面）之上。
        labelzIndex: {
            type: Number,
        },
        // 设置地图语言类型
        lang: {
            type: String,
            default: 'zh_cn',
            validator(val) {
                return ['en', 'zh_cn', 'zh_en'].indexOf(val) >= 0
            },
        },
        // 地图默认鼠标样式。参数defaultCursor应符合CSS的cursor属性规范
        defaultCursor: {
            type: String,
        },
        // 地图显示的参考坐标系
        crs: {
            type: String,
            validator(val) {
                return ['EPSG3857', 'EPSG3395', 'EPSG4326'].indexOf(val) >= 0
            },
        },
        // 地图平移过程中是否使用动画
        animateEnable: {
            type: Boolean,
            default: true,
        },
        // 是否开启地图热点和标注的hover效果
        isHotspot: {
            type: Boolean,
        },
        // 是否监控地图容器尺寸变化
        resizeEnable: {
            type: Boolean,
            default: false,
        },
        // 是否在有矢量底图的时候自动展示室内地图
        showIndoorMap: {
            type: Boolean,
        },
        // 是否支持可以扩展最大缩放级别
        expandZoomRange: {
            type: Boolean,
            default: false,
        },
        // 地图是否可通过鼠标拖拽平移
        dragEnable: {
            type: Boolean,
            default: true,
        },
        // 地图是否可缩放
        zoomEnable: {
            type: Boolean,
            default: true,
        },
        // 地图是否可通过双击鼠标放大地图
        doubleClickZoom: {
            type: Boolean,
            default: true,
        },
        // 地图是否可通过键盘控制
        keyboardEnable: {
            type: Boolean,
            default: true,
        },
        // 地图是否使用缓动效果
        jogEnable: {
            type: Boolean,
            default: true,
        },
        // 地图是否可通过鼠标滚轮缩放浏览
        scrollWheel: {
            type: Boolean,
            default: true,
        },
        // 地图在移动终端上是否可通过多点触控缩放浏览地图
        touchZoom: {
            type: Boolean,
            default: true,
        },
        // 可缺省，当touchZoomCenter=1的时候，手机端双指缩放的以地图中心为中心，否则默认以双指中间点为中心
        touchZoomCenter: {
            type: Number,
        },
        // 设置地图的显示样式
        // 官方常用样式 normal、light、dark
        mapStyle: {
            type: String,
            default: 'amap://styles/light',
        },
        // 设置地图显示3D楼块效果，移动端也可使用
        showBuildingBlock: {
            type: Boolean,
        },
        // 俯仰角度，默认0，[0,83]，2D地图下无效
        pitch: {
            type: Number,
        },
        // 是否允许设置俯仰角度
        pitchEnable: {
            type: Boolean,
        },
        // 楼块出现和消失的时候是否显示动画过程
        buildingAnimation: {
            type: Boolean,
        },
        // 调整天空颜色，配合自定义地图，3D视图有效
        skyColor: {
            type: String,
        },
        // 设置地图的预加载模式，开启预加载的地图会在适当时刻提前加载周边和上一级的地图数据
        preloadMode: {
            type: Boolean,
            default: true,
        },
        // 设置地图模式
        viewMode: {
            type: String,
            default: '2D',
            validator(val) {
                return ['2D', '3D'].indexOf(val) >= 0
            },
        },
        // 插件列表
        plugins: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // state
            loaded: false, // map已创建
            configExclude: ['plugins'],
            // hooks
            onLoad: null,
            // 地图实例
            map: null,
            // 覆盖物集合
            overlay: [],
            // 定位对象集合
            geocoders: [],
            // 绘制对象集合
            mouseTools: [],
        }
    },
    methods: {
        initMap() {
            return (
                this.onLoad
                    // 异步加载插件
                    .then(() => this.plugin(this.plugins))
                    // 创建map实例
                    .then(() => {
                        this._createMap()
                    })
                    .then(() => {
                        this.loaded = true
                        this.$emit('load', this.map)
                    })
            )
        },
        // 添加插件
        plugin(plugins = []) {
            if (plugins.length > 0) {
                return new Promise(resolve => {
                    AMap.plugin(
                        plugins.map(v => 'AMap.' + v),
                        () => resolve()
                    )
                })
            } else {
                return Promise.resolve()
            }
        },
        // 创建地图实例
        _createMap() {
            this.map = new AMap.Map(this.$el, this.config)
            // bind listener
            this._attachEvents(this.map)
        },
        // 清理操作
        _cleanup() {
            // 销毁地图
            if (this.map) {
                this.map.destroy()
            }
        },
    },
    created() {
        if (!window) {
            throw new Error('Amap只能在浏览器中运行')
        }
        if (!this.$_AMap) {
            throw new Error('Amap插件尚未安装')
        }
        if (!this.$_AMap.key) {
            throw new Error('Amap必须提供key')
        }

        // AMap已缓存
        if (this.$_AMap.AMap) {
            return (this.onLoad = this.$_AMap.AMap)
        }

        // 异步加载AMap
        this.onLoad = this.$_AMap.AMap = initAMap(this.$_AMap)
    },
    mounted() {
        this.initMap()
    },
    beforeDestroy() {
        this._cleanup()
    },
}
</script>
